import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import Cookies from 'js-cookie';
import { brandDomain, getSiteSettings } from '@/brands';

// In dev, `/api` is rewritten to the backend by the Vite proxy (see vite.config.ts).
// In production requests go to VITE_API_BASE_URL (the deployed backend host, which
// serves at root with no `/api` prefix). If it's empty, falls back to same-origin.
const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.DEV ? '/api' : import.meta.env.VITE_API_BASE_URL || '',
  prepareHeaders: (headers) => {
    const token = Cookies.get('access-token');

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    // White-label: tell the backend which brand domain this app is served on.
    if (typeof window !== 'undefined') {
      headers.set('X-Brand-Domain', brandDomain());
    }
    return headers;
  },
});

/** sessionStorage key carrying a 401's message across the redirect to /login. */
export const AUTH_ERROR_KEY = 'auth-error';

const baseQueryWithAuth =async (args: any, api: any, extraOptions: any) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result?.error?.status === 401) {
    Cookies.remove('access-token');

    // 🔁 redirect to login — unless the brand has the login page switched off,
    // in which case /login is not mounted and this would land on the 404 page.
    const target = getSiteSettings().showLogin ? '/login' : '/home';
    if (window.location.pathname !== target) {
      // The redirect is a full reload, so the server's reason ("User not found",
      // "account deleted", ...) would vanish and the player would just land back
      // on the login page with nothing explaining why. Carry it across the reload
      // for LoginPage to show.
      const message = (result.error.data as { message?: unknown } | undefined)?.message;
      if (typeof message === 'string' && message.trim()) {
        try {
          sessionStorage.setItem(AUTH_ERROR_KEY, message);
        } catch {
          // storage blocked — the redirect still happens, just without the reason
        }
      }
      window.location.href = target;
    }
  }

  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['User', 'Auth', 'FeatureFlags', 'Sports', 'Bets', 'Account', 'Content', 'FD', 'Affiliate'],
  endpoints: () => ({}),
});

// Export hooks for usage in functional components
export const {
  util: { getRunningQueriesThunk },
} = apiSlice;
// Export the api slice for use in store
export default apiSlice;
